import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { Reveal, PageHero, SectionHead, Eyebrow, CTABand } from "@/components/Shared";
import { experienceList, searchSupport, experienceScale } from "@/data/content";

export default function Experience() {
    return (
        <div data-testid="experience-page">
            <PageHero
                index="04"
                overline="Experience"
                title="Complex matters. Real investigative experience."
                lead="For confidentiality reasons, client names and sensitive matter details are not disclosed unless appropriate authorisation is available."
                testId="experience-hero"
            />

            {/* OUR EXPERIENCE INCLUDES */}
            <section className="blueprint-grid relative overflow-hidden" data-testid="experience-list-section">
                <div className="ambient-glow -right-40 -top-24 h-[400px] w-[400px] bg-[#1D4F9C]/22" aria-hidden />
                <div className="relative mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28">
                <SectionHead
                    index="01"
                    overline="Our Experience Includes"
                    title="Investigative matters across disciplines."
                    testId="experience-list"
                />
                <div className="mt-14 grid gap-px border border-[#243650] bg-[#243650] sm:grid-cols-2 lg:grid-cols-3">
                    {experienceList.map((item, i) => (
                        <Reveal key={item} delay={(i % 3) * 0.05}>
                            <div
                                className="group flex h-full items-center justify-between gap-4 bg-[#0F1B2E] px-6 py-5 transition-colors duration-300 hover:bg-[#163050]"
                                data-testid={`experience-item-${i}`}
                            >
                                <span className="flex items-center gap-4">
                                    <span className="font-tech text-[10px] tracking-[0.18em] text-[#D9A84E]">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="text-sm text-[#E8ECF4] transition-colors duration-300 group-hover:text-white">
                                        {item}
                                    </span>
                                </span>
                                <ArrowUpRight className="h-4 w-4 shrink-0 text-[#8C97AC] transition-colors duration-300 group-hover:text-[#D9B36A]" />
                            </div>
                        </Reveal>
                    ))}
                </div>
                </div>
            </section>

            {/* SEARCH-RELATED INVESTIGATIVE SUPPORT */}
            <section className="border-y border-[#243650] bg-[#0F1B2E]" data-testid="search-support-section">
                <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28">
                    <Reveal>
                        <div className="certificate p-10 lg:p-14" data-testid="search-support-card">
                            <ShieldCheck className="h-7 w-7 text-[#D9A84E]" />
                            <Eyebrow index="02" label={searchSupport.title} />
                            <p className="mt-6 max-w-4xl text-base leading-relaxed text-[#9AA7BF]">
                                {searchSupport.body}
                            </p>
                            <p className="mt-8 border-t border-[#243650] pt-6 text-xs italic leading-relaxed text-[#8C97AC]" data-testid="search-support-disclaimer">
                                {searchSupport.disclaimer}
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SCALE OF EXPERIENCE */}
            <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28" data-testid="scale-section">
                <SectionHead
                    index="03"
                    overline="Scale of Experience"
                    title={experienceScale.heading}
                    lead={experienceScale.body}
                    testId="scale"
                />
                <div className="mt-14 grid gap-px border border-[#243650] bg-[#243650] sm:grid-cols-3">
                    {experienceScale.stats.map((s, i) => (
                        <Reveal key={s.label} delay={i * 0.08}>
                            <div className="h-full bg-[#0F1B2E] p-9" data-testid={`scale-stat-${i}`}>
                                <p className="font-display text-4xl font-bold text-[#5B93E8] sm:text-5xl">
                                    {s.value}
                                </p>
                                <p className="mt-4 font-tech text-[10px] uppercase leading-loose tracking-[0.2em] text-[#8C97AC]">
                                    {s.label}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal className="mt-8">
                    <p className="max-w-3xl text-xs italic leading-relaxed text-[#8C97AC]" data-testid="scale-footnote">
                        {experienceScale.footnote}
                    </p>
                </Reveal>
            </section>

            <CTABand
                title="Discuss an Investigation."
                lead="Complex matters. Real investigative experience. Evidence-based findings."
                testId="experience-cta"
            />
        </div>
    );
}
